/**
 * Phase 8A — Commerce V2 (Breed Collection V2 / Memorial Product V2).
 *
 * Entirely new, isolated script for the new traditional-commerce
 * templates. Initializes ONLY when a [data-commerce-v2] root is
 * present on the page (never on a standard product/collection page,
 * never on the Memorial Builder). No global Builder state is read or
 * written, no Builder function is called, no Builder DOM structure is
 * relied upon — this file has zero dependency on assets/bpf-memorial-
 * designer.js or assets/bpf-memorial-designer.css.
 *
 * Reuses exactly one piece of existing shared theme infrastructure at
 * runtime: the sitewide cart drawer/notification element's own
 * renderContents() method (document.querySelector('cart-drawer') /
 * ('cart-notification')) — the same generic, Builder-independent cart
 * UI every other page on this storefront already uses. Everything
 * else (media gallery, option/variant matching, Picture Frame add-on,
 * Add to Cart submission) is implemented independently here.
 */
(function () {
  'use strict';

  function debounce(fn, wait) {
    var t = null;
    return function () {
      var args = arguments;
      var ctx = this;
      if (t) clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  // Heuristic-only, cosmetic swatch color — a text label always
  // accompanies every swatch (accessibility requirement), so an
  // unmatched color simply renders a neutral dot rather than blocking
  // anything.
  // Minimal, self-contained cents -> "$X.XX" formatter — this
  // storefront's prices are USD; no dependency on a global money-
  // format setting this file can't confirm exists. Matches the same
  // plain "$15.00"-style formatting already used elsewhere in this
  // codebase's own verified fallback data.
  function formatMoney(cents) {
    var dollars = (cents / 100).toFixed(2);
    var parts = dollars.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  // ----- Phase 8H, Part C — opt-in, developer-only cart-drawer render
  // diagnostics. Inert unless a developer explicitly sets
  // `window.__BPF_V2_CART_DEBUG__ = true` in the console BEFORE
  // clicking Add to Cart — never active for a real customer, never
  // referenced from anywhere else in the theme, and every call here is
  // a no-op (console.log only, no DOM writes) when the flag is unset.
  // Exists to answer, with real runtime evidence rather than another
  // guess, the open question this phase exists to resolve: does
  // Memorial Product V2's post-add drawer content NEVER render, or
  // does it render correctly and then get cleared/overwritten by
  // something else?
  function isCartDebugEnabled() {
    return typeof window !== 'undefined' && window.__BPF_V2_CART_DEBUG__ === true;
  }

  // Phase 8K, Part Y — same opt-in, developer-only, console-log-only
  // pattern as isCartDebugEnabled() above, for the Name Engraving
  // feature specifically. Inert unless window.__BPF_V2_ENGRAVING_DEBUG__
  // is explicitly set to true; never active for a real customer.
  function isEngravingDebugEnabled() {
    return typeof window !== 'undefined' && window.__BPF_V2_ENGRAVING_DEBUG__ === true;
  }

  function cartDebug() {
    if (!isCartDebugEnabled() || typeof console === 'undefined') return;
    var args = ['[bpf-v2-cart-debug]'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  // Part G — observes the actual drawer target element for mutations
  // for a short window after render, to distinguish "content never
  // rendered" from "content rendered, then something else cleared it."
  // Disconnects itself automatically; never left running.
  function observeDrawerForClearing(targetEl, windowMs) {
    if (!isCartDebugEnabled() || !targetEl || typeof MutationObserver !== 'function') return;
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        cartDebug('drawer target mutation observed', {
          type: mutation.type,
          addedNodes: mutation.addedNodes.length,
          removedNodes: mutation.removedNodes.length,
          targetInnerHTMLLength: targetEl.innerHTML.length,
        });
      });
    });
    observer.observe(targetEl, { childList: true, subtree: true });
    setTimeout(function () {
      observer.disconnect();
      cartDebug('drawer target MutationObserver window closed', { windowMs: windowMs, finalInnerHTMLLength: targetEl.innerHTML.length });
    }, windowMs);
  }

  // Part C — logs the drawer's own target-element content length at
  // several points in time (synchronously after render, next animation
  // frame, and ~100ms later) so a "renders then gets cleared" defect
  // shows up as a length that goes from >0 back to 0 across these
  // checkpoints, distinct from "never rendered" (0 at every checkpoint).
  function scheduleDrawerLengthChecks(targetEl, label) {
    if (!isCartDebugEnabled() || !targetEl) return;
    cartDebug(label + ' — immediately after render', { innerHTMLLength: targetEl.innerHTML.length });
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(function () {
        cartDebug(label + ' — next animation frame', { innerHTMLLength: targetEl.innerHTML.length });
      });
    }
    setTimeout(function () {
      cartDebug(label + ' — ~100ms after render', { innerHTMLLength: targetEl.innerHTML.length });
    }, 100);
  }

  var SWATCH_KEYWORDS = [
    { test: /black/i, color: '#1c1c1c' },
    { test: /white/i, color: '#f5f4ef' },
    { test: /gold/i, color: '#c9a24b' },
    { test: /blue/i, color: '#3b5f8a' },
    { test: /pink/i, color: '#d98ca0' },
  ];

  // Global Text/Engraving Color defaulting rule — the Text/Engraving
  // Color should default to whichever Pet/Animal Figurine Color is
  // currently selected (see initPanel()'s syncEngravingColorToPetColor()
  // for the event-based sync itself). Live-data-audited directly against
  // real Shopify products (a real Vault and a real Capsule, 2026-09-26):
  // the "Color" option (Pet/Animal Figurine Color) and the "Engraving
  // Color" line-item property already use the IDENTICAL canonical value
  // strings — White, Black, Metallic Blue, Metallic Pink, Metallic Gold
  // — so this is currently an identity mapping. It's still written out
  // explicitly, as one authoritative table, rather than assumed inline:
  // if the two value sets ever diverge, an unrecognized pet-color value
  // simply has no entry here and syncEngravingColorToPetColor() skips
  // defaulting for it (never guesses/falls back to the wrong color).
  // Never applies to "Vault Color" (Vault panels' OTHER, unrelated real
  // Shopify option) or any Frame/Urn/Pedestal color control.
  var PET_COLOR_TO_ENGRAVING_COLOR = {
    White: 'White',
    Black: 'Black',
    'Metallic Blue': 'Metallic Blue',
    'Metallic Pink': 'Metallic Pink',
    'Metallic Gold': 'Metallic Gold',
  };

  function applySwatchColors(root) {
    var swatches = root.querySelectorAll('[data-swatch]');
    swatches.forEach(function (el) {
      var value = el.getAttribute('data-swatch') || '';
      var match = SWATCH_KEYWORDS.find(function (entry) {
        return entry.test.test(value);
      });
      el.style.backgroundColor = match ? match.color : '#c7c2b8';
    });
  }

  // Phase 8C/8F — the total docked-media MODULE height (hero +
  // thumbnails + the internal gap between them, together — never hero
  // alone plus thumbnails on top of that, per the phase spec's explicit
  // warning). Formula (Phase 8F, Part F): usableViewportHeight is the
  // real visual viewport height alone — NOT minus header height.
  // Runtime audit (sections/header.liquid's own StickyHeader class,
  // read-only, unmodified) confirms the site header HIDES itself once
  // scrolled past, rather than remaining persistently fixed above page
  // content, so there is no real persistent top offset to subtract by
  // the time the media is docked (Phase 8D/8E measured and subtracted
  // the INITIAL header height here, which is what caused Phase 8E's own
  // "docks too early, leaves a permanent gap" defect once the header
  // scrolled away). target = usableViewportHeight * 0.40; clamped to
  // [MIN, MAX] so a short landscape viewport never collapses the
  // gallery into uselessness and a very tall phone never produces an
  // oversized one.
  var BPF_V2_STICKY_MEDIA_MIN_PX = 220;
  var BPF_V2_STICKY_MEDIA_MAX_PX = 380;
  var BPF_V2_STICKY_MEDIA_RATIO = 0.4;

  // ----- Docked mobile media module height -----
  // visualViewport.height is preferred over window.innerHeight where
  // available — more reliable during mobile Safari's URL-bar show/hide
  // and, per Part L, always reflects the real storefront iframe's own
  // content viewport even when rendered inside the Shopify Theme
  // Editor's preview iframe (the editor's own outer admin chrome is a
  // separate window this script never has or needs access to).
  function measureStickyOffset(root) {
    var usableViewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    var target = usableViewportHeight * BPF_V2_STICKY_MEDIA_RATIO;
    var total = Math.min(BPF_V2_STICKY_MEDIA_MAX_PX, Math.max(BPF_V2_STICKY_MEDIA_MIN_PX, target));
    root.style.setProperty('--bpf-v2-sticky-media-total', Math.round(total) + 'px');
  }

  // ----- Mobile media docking (Phase 8F, Part C) -----
  // .bpf-commerce-v2__media-col itself NEVER becomes position:fixed and
  // never leaves normal document flow — only the visible .media block
  // inside it does, once the CSS-driving `.is-docked` class is present.
  // A 1px sentinel (media-col's own first child, always in normal flow)
  // gives an IntersectionObserver a stable signal for the media's TRUE
  // natural document position: when the sentinel scrolls above the
  // viewport top, dock; when it scrolls back into view, undock. Because
  // media-col's own top edge never moves (only its height changes to
  // reserve space while docked — see the CSS file's own comment), this
  // produces zero feedback loop between docking and the sentinel's own
  // reported position, and therefore no oscillation/flicker at the
  // threshold (Part S).
  function initMobileDock(root) {
    var mediaCol = root.querySelector('[data-commerce-v2-media-col]');
    var sentinel = root.querySelector('[data-commerce-v2-media-sentinel]');
    if (!mediaCol || !sentinel || typeof IntersectionObserver !== 'function') return;

    var mobileQuery = window.matchMedia('(max-width: 749px)');

    function setDocked(docked) {
      mediaCol.classList.toggle('is-docked', docked);
    }

    var observer = new IntersectionObserver(function (entries) {
      if (!mobileQuery.matches) {
        // Desktop uses its own, separate position:sticky rule — never
        // dock on desktop, and immediately clear a stale docked state
        // if the viewport crossed the breakpoint while docked.
        setDocked(false);
        return;
      }
      var entry = entries[entries.length - 1];
      if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
        setDocked(true);
      } else if (entry.isIntersecting) {
        setDocked(false);
      }
    }, { threshold: 0 });

    observer.observe(sentinel);

    var onBreakpointChange = function (event) {
      if (!event.matches) setDocked(false);
    };
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', onBreakpointChange);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(onBreakpointChange);
    }
  }

  // ----- Mobile sticky Add to Cart (Breed Collection V2 only) -----
  // Mirrors initMobileDock() above: the dock wrapper (.cta-dock) never
  // itself becomes position:fixed and never leaves normal document
  // flow — only .cta-inner, wrapping the EXACT same real, unmodified
  // submit button, becomes fixed once a 1px sentinel (the dock's own
  // first child, always in flow) reports via IntersectionObserver that
  // the button's true natural position isn't currently on screen.
  //
  // Unlike the media dock (fixed to the TOP, docks only once scrolled
  // UP PAST it — an intentionally asymmetric check, see that
  // function's own comment), this docks to the BOTTOM whenever the
  // button's natural position is off-screen in EITHER direction: on
  // initial load, before the shopper has scrolled down to it, and
  // again if they scroll back up past it — matching a conventional
  // sticky Add to Cart bar. A plain "not intersecting" check (no
  // above/below distinction needed) correctly covers both.
  //
  // .cta-dock only exists in the DOM at all when section.settings.mode
  // == 'collection' (see the .liquid file's own guard) — Memorial
  // Product V2 renders the original, unwrapped button and never gets
  // this behavior, satisfying the "Breed Collection V2 only" scope
  // without a second code path here.
  //
  // Collection mode's two memorial-type panels (Vault/Capsule) each
  // render their own real form/button; initTypeSwitch() only ever
  // toggles [hidden] on whichever isn't active (never removes/
  // recreates either), so observing every dock up front and skipping
  // whichever one's ancestor panel is currently hidden (offsetParent
  // === null, reliable here since .cta-dock itself never becomes
  // position:fixed) correctly follows whichever product is selected
  // with no re-targeting needed when the shopper switches.
  function measureCtaOffset(dock, inner) {
    var rect = inner.getBoundingClientRect();
    if (rect.height > 0) {
      dock.style.setProperty('--bpf-v2-sticky-cta-total', Math.round(rect.height) + 'px');
    }
  }

  function initStickyCta(root) {
    var docks = Array.prototype.slice.call(root.querySelectorAll('[data-commerce-v2-cta-dock]'));
    if (!docks.length || typeof IntersectionObserver !== 'function') return;

    var mobileQuery = window.matchMedia('(max-width: 749px)');

    var entries = docks
      .map(function (dock) {
        return {
          dock: dock,
          sentinel: dock.querySelector('[data-commerce-v2-cta-sentinel]'),
          inner: dock.querySelector('[data-commerce-v2-cta-inner]'),
        };
      })
      .filter(function (entry) {
        return entry.sentinel && entry.inner;
      });
    if (!entries.length) return;

    function setDocked(entry, docked) {
      if (docked) measureCtaOffset(entry.dock, entry.inner);
      entry.dock.classList.toggle('is-docked', docked);
    }

    function findEntry(sentinel) {
      return entries.filter(function (entry) {
        return entry.sentinel === sentinel;
      })[0];
    }

    // Directional docking decision (replaces a plain !isIntersecting
    // check, which docked whenever the sentinel was off-screen in
    // EITHER direction — including after the shopper had already
    // scrolled PAST the real button, which incorrectly brought the
    // sticky bar back). Only "below the viewport" should ever dock;
    // "above the viewport" (already scrolled past) must stay
    // undocked. rect.top is compared against the viewport's own
    // height — visualViewport.height is preferred over innerHeight
    // where available, matching this same file's existing
    // measureStickyOffset() preference for mobile Safari's URL-bar
    // show/hide reliability. Takes a plain rect (from either the
    // IntersectionObserver entry's own boundingClientRect — no extra
    // getBoundingClientRect() call needed for the common case — or a
    // fresh one on resize, see below) rather than reading the DOM
    // itself, so both call sites share one source of truth. */
    function isBelowViewport(rect) {
      var viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      return rect.top >= viewportHeight;
    }

    function evaluate(entry, rect) {
      if (!mobileQuery.matches) {
        // Desktop uses no docking at all here — the CTA stays
        // exactly where it already renders today.
        setDocked(entry, false);
        return;
      }
      // A hidden panel's sentinel/dock has no layout box at all —
      // never dock the memorial type the shopper isn't even
      // viewing, which would otherwise show a second fixed bar for
      // the WRONG product the instant its real button scrolls
      // off-screen while hidden.
      if (entry.dock.offsetParent === null) {
        setDocked(entry, false);
        return;
      }
      setDocked(entry, isBelowViewport(rect));
    }

    var observer = new IntersectionObserver(function (observedEntries) {
      observedEntries.forEach(function (observed) {
        var entry = findEntry(observed.target);
        if (!entry) return;
        // threshold: 0 fires exactly at every 0%-visibility crossing
        // — entering/leaving via the top OR the bottom — and the
        // entry's own boundingClientRect (no separate DOM read) is
        // enough to tell which one just happened, in either scroll
        // direction: down past the button (top goes negative -> stays
        // undocked), back up past it again (top reaches/exceeds the
        // viewport height -> re-docks), or first reaching it from
        // either direction (top lands inside [0, viewportHeight) ->
        // undocks).
        evaluate(entry, observed.boundingClientRect);
      });
    }, { threshold: 0 });

    entries.forEach(function (entry) {
      observer.observe(entry.sentinel);
    });

    // Manual re-evaluation, for the two cases the IntersectionObserver
    // above can't be relied on to fire for by itself:
    //
    // 1. Resize/orientation change (see initRoot()'s own debounced
    //    resize/visualViewport listener, which already calls this) —
    //    a viewport-height change alone can shift the below-viewport
    //    threshold without the sentinel's own position on the page
    //    changing, so it may never re-cross 0% visibility on its own.
    //
    // 2. Vault/Capsule switching (see initTypeSwitch()'s own
    //    setActiveType(), which calls this too) — toggling `hidden`
    //    elsewhere in the DOM moves the newly-shown panel's sentinel
    //    from a zero-size, non-participating box straight to its real
    //    on-screen position without ever crossing a scroll-driven
    //    threshold, so the observer callback above is not reliably
    //    fired immediately (confirmed: the previously-reported "sticky
    //    CTA only appears after a scroll/swipe" symptom).
    //
    // Both cases reuse this SAME one entries/evaluate() pair (never a
    // second observer, never a second CTA) with a fresh
    // getBoundingClientRect() per entry — cheap, and only ever run on
    // an already-throttled resize or an explicit user action, never on
    // every scroll frame.
    root._reevaluateStickyCta = function () {
      entries.forEach(function (entry) {
        evaluate(entry, entry.sentinel.getBoundingClientRect());
      });
    };

    var onBreakpointChange = function (event) {
      if (!event.matches) {
        entries.forEach(function (entry) {
          setDocked(entry, false);
        });
      }
    };
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', onBreakpointChange);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(onBreakpointChange);
    }
  }

  // ----- Media gallery (per media block; a collection-mode page has one per candidate product) -----
  function initMedia(mediaEl) {
    var main = mediaEl.querySelector('[data-commerce-v2-media-main]');
    var thumbs = mediaEl.querySelectorAll('[data-media-target]');
    if (!main) return;

    // Phase 8C, Part G — the server-rendered initial hero (see
    // snippets/bpf-memorial-commerce-v2-media.liquid's own hero-
    // selection priority) is captured once, up front, so switching
    // Vault <-> Capsule and back can always restore THIS gallery to
    // its own correct default rather than wherever a manual thumbnail
    // click last left it — never a stale index bled in from the OTHER
    // product's gallery (a structurally separate DOM tree already
    // makes literal cross-contamination impossible; this is the
    // "reset to this product's own default" behavior the phase spec
    // additionally asks for).
    var initialActive = main.querySelector('.bpf-commerce-v2__media-item.is-active');
    var initialMediaId = initialActive ? initialActive.dataset.mediaId : null;

    // Phase 8E, Part C — root cause of "3D thumbnail selects but the
    // main media area is blank": the theme's generic <deferred-media>/
    // <product-model> (assets/global.js) only ever loads its real
    // content (clones its own <template> into the DOM) when ITS OWN
    // internal poster button is clicked — our external thumbnail rail
    // only toggled which wrapper was visible, so the interactive
    // model/video never actually loaded. Calling the SAME generic
    // loadContent() method the poster's own click handler already
    // calls (never a second/competing load path) makes activating the
    // item load it immediately, matching every other media type's
    // "select = shows" behavior. Guarded by the element's own [loaded]
    // attribute, so switching away and back never reloads/re-clones.
    function loadRichMediaIfNeeded(target) {
      var richMedia = target.querySelector('deferred-media, product-model');
      if (richMedia && typeof richMedia.loadContent === 'function' && !richMedia.hasAttribute('loaded')) {
        richMedia.loadContent(false);
      }
    }

    function setActive(mediaId) {
      var target = main.querySelector('[data-media-id="' + mediaId + '"]');
      if (!target) return;
      // Phase 8D — a playing video/3D model must stop when the
      // gallery moves away from it (the same generic behavior the
      // theme's own product-media gallery already provides elsewhere);
      // window.pauseAllMedia is the same generic, sitewide helper
      // (assets/global.js), never a V2-specific reimplementation.
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      main.querySelectorAll('[data-media-id]').forEach(function (el) {
        el.classList.remove('is-active');
      });
      target.classList.add('is-active');
      thumbs.forEach(function (btn) {
        btn.setAttribute('aria-current', btn.getAttribute('data-media-target') === String(mediaId) ? 'true' : 'false');
      });
      loadRichMediaIfNeeded(target);
    }

    // The server may have already resolved a rich-media item as the
    // initial hero (see the media snippet's own hero-selection
    // priority) — that item starts with .is-active server-rendered, so
    // setActive() above is never called for it. Without this, the very
    // first model/video a customer sees on page load would ALSO be
    // blank until they clicked away and back.
    //
    // Phase 8J, Part AF — `!mediaEl.hidden` guard: in collection mode,
    // initRoot() calls initMedia() for BOTH candidates up front, but
    // only the default one is visible — the other's whole .media block
    // carries the `hidden` attribute (display:none). Eagerly loading a
    // model INSIDE a display:none block measures 0x0, which real-device
    // audit found is a genuine contributing cause of a blank/non-
    // functional viewer. Skipping it here is safe: initTypeSwitch()'s
    // own setActiveType() already clears `hidden` BEFORE calling
    // _resetToInitialHero() (which calls setActive() ->
    // loadRichMediaIfNeeded()), so a hidden candidate's model correctly
    // loads at the moment it actually becomes visible instead.
    if (initialActive && initialActive.classList.contains('bpf-commerce-v2__media-item--rich') && !mediaEl.hidden) {
      loadRichMediaIfNeeded(initialActive);
    }

    thumbs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setActive(btn.getAttribute('data-media-target'));
      });
    });

    mediaEl._setActiveMedia = setActive;
    mediaEl._resetToInitialHero = function () {
      if (initialMediaId) setActive(initialMediaId);
      var thumbRail = mediaEl.querySelector('[data-commerce-v2-thumbnails]');
      if (thumbRail) thumbRail.scrollLeft = 0;
    };
  }

  // ----- One product panel (options/variant matching/frame add-on/submit) -----
  function initPanel(panelEl, root) {
    var form = panelEl.querySelector('[data-commerce-v2-form]');
    if (!form) return;

    var variantIdInput = form.querySelector('[data-commerce-v2-variant-id]');
    var variantsScript = form.querySelector('[data-commerce-v2-variants]');
    var optionNamesScript = form.querySelector('[data-commerce-v2-option-names]');
    var priceEl = panelEl.querySelector('[data-commerce-v2-price]');
    var submitButton = form.querySelector('[data-commerce-v2-submit]');
    var submitLabel = form.querySelector('[data-commerce-v2-submit-label]');
    var spinner = form.querySelector('[data-commerce-v2-spinner]');
    var errorEl = form.querySelector('[data-commerce-v2-error]');

    var variants = [];
    var optionNames = [];
    try {
      if (variantsScript) variants = JSON.parse(variantsScript.textContent) || [];
    } catch (e) {
      variants = [];
    }
    try {
      if (optionNamesScript) optionNames = JSON.parse(optionNamesScript.textContent) || [];
    } catch (e) {
      optionNames = [];
    }

    var optionGroups = Array.prototype.slice.call(form.querySelectorAll('[data-commerce-v2-option]'));

    function getSelectedOptionValues() {
      // Ordered to match optionNames (option1/option2/option3 positions).
      return optionNames.map(function (name) {
        var group = optionGroups.filter(function (g) {
          return g.getAttribute('data-option-name') === name;
        })[0];
        if (!group) return null;
        var checked = group.querySelector('[data-option-value]:checked');
        return checked ? checked.value : null;
      });
    }

    // Root-cause fix (real order #1137 gap) — reuses the SAME already-
    // resolved selection state getSelectedOptionValues()/findMatchingVariant()
    // already use for the real cart-add path, keyed by each option's own
    // real Shopify name (e.g. "Color", "Vault Color" for Vault; whatever
    // Capsule's own options are named) — never re-derived from a display
    // label after the fact. Used ONLY by the partner-save payload below;
    // the real /cart/add.js properties (collectProperties()) are
    // completely unchanged.
    function collectSelectedVariantOptions() {
      var values = getSelectedOptionValues();
      var map = {};
      optionNames.forEach(function (name, index) {
        if (values[index] !== null && values[index] !== undefined) map[name] = values[index];
      });
      return map;
    }

    function findMatchingVariant() {
      var selected = getSelectedOptionValues();
      return variants.filter(function (variant) {
        return selected.every(function (value, index) {
          if (value === null) return true;
          var key = 'option' + (index + 1);
          return variant[key] === value;
        });
      })[0] || null;
    }

    var mediaEl = root.querySelector('[data-commerce-v2-media][data-memorial-type="' + panelEl.getAttribute('data-memorial-type') + '"]');
    var totalAmountEl = form.querySelector('[data-commerce-v2-total-amount]');
    var currentMainPriceCents = null;

    // Phase 8C, Part H — combined purchase total. Presentation only:
    // Shopify's own real cart-line prices are never touched here, this
    // only sums the two ALREADY-RESOLVED real prices for display next
    // to the CTA.
    function updateTotal() {
      if (!totalAmountEl || currentMainPriceCents === null) return;
      var total = currentMainPriceCents;
      if (frameToggle && frameToggle.checked) {
        var checkedFrameColor = form.querySelector('[data-frame-variant-id]:checked');
        var frameCents = checkedFrameColor ? parseInt(checkedFrameColor.getAttribute('data-frame-price'), 10) : NaN;
        if (!isNaN(frameCents)) total += frameCents;
      }
      totalAmountEl.textContent = formatMoney(total);
    }

    var isSubmitting = false;

    // Root-level single source of truth for the CTA label (see
    // initPartnerSync()/refreshPartnerState() below) — the ONLY place
    // that writes submitLabel.textContent. Both updateFromVariant() and
    // setLoading() call this instead of hardcoding "Add to Cart"/
    // "Sold Out"/"Adding…" themselves, so a variant change or a submit
    // in flight can never silently strand the partner label back on the
    // normal-BPF text with nothing to restore it afterward.
    function refreshSubmitLabel() {
      if (!submitLabel) return;
      if (isSubmitting) {
        submitLabel.textContent = 'Adding…';
        return;
      }
      if (root._partnerActive && window.BPFPartnerMode) {
        submitButton.classList.add('bpf-partner-cta');
        submitLabel.textContent = window.BPFPartnerMode.isPartnerWindowMode()
          ? 'Save Memorial & Continue to ' + window.BPFPartnerMode.getDisplayName()
          : 'Return to ' + window.BPFPartnerMode.getDisplayName() + ' to Purchase';
        return;
      }
      var variant = findMatchingVariant();
      var unavailable = Boolean(variant && variant.available === false);
      submitLabel.textContent = unavailable ? 'Sold Out' : 'Add to Cart';
    }

    // Root-level single source of truth for the mandatory Picture Frame
    // add-on (Phase 2C.2A — partner memorials only, 1 Vault/Capsule PLUS
    // 1 Picture Frame, included, non-removable). Never forces the toggle
    // OFF — normal, non-partner BPF behavior (optional frame, fully
    // user-controlled) is completely unchanged when root._partnerActive
    // is false.
    function refreshFrameLock() {
      if (!frameToggle || !root._partnerActive) return;
      frameToggle.checked = true;
      frameToggle.disabled = true;
      frameToggle.setAttribute('aria-disabled', 'true');
      if (frameOptions) frameOptions.hidden = false;
      var frameWrap = form.querySelector('[data-commerce-v2-frame]');
      if (frameWrap) frameWrap.classList.add('bpf-commerce-v2__frame-addon--required');
      var frameTitle = form.querySelector('.bpf-commerce-v2__frame-title');
      if (frameTitle) frameTitle.textContent = 'Picture Frame — Included';
      var frameMeta = form.querySelector('.bpf-commerce-v2__frame-meta');
      if (frameMeta) frameMeta.textContent = 'Included with your partner memorial';
      updateTotal();
    }

    function updateFromVariant() {
      var variant = findMatchingVariant();
      if (!variant) return;

      if (variantIdInput) variantIdInput.value = variant.id;

      if (submitButton) {
        var unavailable = variant.available === false;
        submitButton.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
        refreshSubmitLabel();
      }

      if (typeof variant.price === 'number') {
        currentMainPriceCents = variant.price;
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        updateTotal();
      }

      // Phase 8C, Part F — variant media sync. A real featured_media on
      // the newly-resolved variant switches the gallery to it; when
      // absent, the gallery is left exactly as it was (Part: "do NOT
      // randomly jump") — manual thumbnail selections and the
      // server-resolved default hero both survive untouched.
      if (mediaEl && mediaEl._setActiveMedia && variant.featured_media && variant.featured_media.id) {
        mediaEl._setActiveMedia(variant.featured_media.id);
      }
    }

    optionGroups.forEach(function (group) {
      group.querySelectorAll('[data-option-value]').forEach(function (input) {
        input.addEventListener('change', updateFromVariant);
      });
    });

    // Pet Name counter — Phase 8J: rendered for any Name-Engraving-
    // eligible panel (every Vault, plus a Capsule the centralized
    // v2_name_engraving_eligible resolver approved), not Vault-only
    // any more. See snippets/bpf-memorial-commerce-v2-options.liquid's
    // own header comment for the full resolver.
    var petNameInput = form.querySelector('[data-commerce-v2-pet-name]');
    var petNameCounter = form.querySelector('[data-commerce-v2-pet-name-counter]');
    if (petNameInput && petNameCounter) {
      var maxLength = parseInt(petNameInput.getAttribute('maxlength'), 10) || 12;
      var updateCounter = function () {
        petNameCounter.textContent = petNameInput.value.length + ' / ' + maxLength;
      };
      petNameInput.addEventListener('input', updateCounter);
      updateCounter();
    }

    // Badge Number — Emblem Police template only (this input simply
    // doesn't exist in the DOM on any other template, so this whole
    // block no-ops there). Independent of Pet Name: own counter, own
    // 4-char/alphanumeric enforcement. maxlength="4" in the liquid
    // already blocks normal typing past 4 characters; this additionally
    // strips non-alphanumeric characters and defends paste/programmatic
    // input against exceeding 4 characters, so "a fifth character cannot
    // be entered or submitted" holds even outside normal keystrokes.
    var badgeNumberInput = form.querySelector('[data-commerce-v2-badge-number]');
    var badgeNumberCounter = form.querySelector('[data-commerce-v2-badge-number-counter]');
    if (badgeNumberInput && badgeNumberCounter) {
      var badgeMaxLength = parseInt(badgeNumberInput.getAttribute('maxlength'), 10) || 4;
      var updateBadgeNumberCounter = function () {
        badgeNumberCounter.textContent = badgeNumberInput.value.length + ' / ' + badgeMaxLength;
      };
      badgeNumberInput.addEventListener('input', function () {
        var sanitized = badgeNumberInput.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, badgeMaxLength);
        if (sanitized !== badgeNumberInput.value) badgeNumberInput.value = sanitized;
        updateBadgeNumberCounter();
      });
      updateBadgeNumberCounter();
    }

    // Phase 8J/8K, Parts I/J/K/X — conditional Engraving Color. The
    // NATIVE V2 Engraved Pet Name input's own current value is the SOLE
    // trigger — never Globo's separate/temporary field, never which
    // color happens to be checked, never page-load timing on its own.
    // Collapsed (and every radio disabled) whenever
    // petNameInput.value.trim().length is 0 — a spaces-only value must
    // NOT expand it. A previously-made color selection is deliberately
    // left checked when the name is cleared (Part K: "acceptable to
    // retain their previous in-memory color selection") — disabling the
    // inputs already prevents it from being interacted with while
    // collapsed, and collectProperties() below is the actual,
    // authoritative gate on whether it is ever SUBMITTED. Called once
    // on init — Part X: this correctly re-syncs to a browser-restored/
    // back-forward-cache-populated name value instead of assuming
    // collapsed — and again on every input event thereafter.
    var engravingColorWrap = form.querySelector('[data-commerce-v2-engraving-color]');
    var engravingColorInputs = engravingColorWrap ? Array.prototype.slice.call(engravingColorWrap.querySelectorAll('[data-commerce-v2-engraving-color-input]')) : [];
    if (petNameInput && engravingColorWrap) {
      var updateEngravingColorVisibility = function () {
        var hasName = petNameInput.value.trim().length > 0;
        engravingColorWrap.classList.toggle('is-expanded', hasName);
        engravingColorInputs.forEach(function (input) {
          input.disabled = !hasName;
        });
        if (isEngravingDebugEnabled()) {
          console.log('[bpf-v2-engraving-debug] visibility updated', {
            trimmedName: petNameInput.value.trim(),
            expanded: hasName,
          });
        }
      };
      petNameInput.addEventListener('input', updateEngravingColorVisibility);
      updateEngravingColorVisibility();
    }

    // Global Text/Engraving Color defaulting rule — defaults Engraving
    // Color to the currently-selected Pet/Animal Figurine Color
    // (PET_COLOR_TO_ENGRAVING_COLOR above), and re-defaults every time
    // that figurine color actually changes, without ever overwriting a
    // customer's own manual Engraving Color pick except on the NEXT
    // figurine-color change. Scoped to a DEDICATED 'change' listener on
    // ONLY the real Shopify "Color" option's own radios — deliberately
    // separate from the shared optionGroups 'change' -> updateFromVariant
    // wiring above, so this never fires for "Vault Color" or any other
    // option, and never re-runs on unrelated updates (frame, quantity,
    // loading state, media, disclosures). Sets .checked directly (no
    // simulated click/dispatchEvent) — collectProperties() already reads
    // whichever Engraving Color radio is :checked, exactly as if the
    // customer had picked it themselves, so cart/order properties and
    // the partner draft payload (which reuses collectProperties()) both
    // automatically agree with zero partner-specific or payload-specific
    // wiring.
    var petColorGroup = optionGroups.filter(function (group) {
      return group.getAttribute('data-option-name') === 'Color';
    })[0];
    if (petColorGroup && engravingColorInputs.length > 0) {
      var syncEngravingColorToPetColor = function () {
        var checkedPetColor = petColorGroup.querySelector('[data-option-value]:checked');
        if (!checkedPetColor) return;
        var mappedEngravingColor = PET_COLOR_TO_ENGRAVING_COLOR[checkedPetColor.value];
        if (!mappedEngravingColor) return; // no corresponding Engraving Color option for this pet color — never invents/falls back to a wrong color
        var targetEngravingColorInput = engravingColorInputs.filter(function (input) {
          return input.value === mappedEngravingColor;
        })[0];
        if (targetEngravingColorInput) targetEngravingColorInput.checked = true;
      };
      petColorGroup.querySelectorAll('[data-option-value]').forEach(function (input) {
        input.addEventListener('change', syncEngravingColorToPetColor);
      });
      syncEngravingColorToPetColor();
    }

    // Phase 8K, Part Y — opt-in, developer-only console diagnostic.
    // Inert unless a developer explicitly sets
    // window.__BPF_V2_ENGRAVING_DEBUG__ = true; never active for a real
    // customer, never renders any visible markup of its own.
    if (isEngravingDebugEnabled() && panelEl.hasAttribute('data-engraving-eligible-reason')) {
      console.log('[bpf-v2-engraving-debug] panel eligibility', {
        memorialType: panelEl.getAttribute('data-memorial-type'),
        eligibleReason: panelEl.getAttribute('data-engraving-eligible-reason'),
        nativeNameFieldFound: Boolean(petNameInput),
        engravingColorControlFound: Boolean(engravingColorWrap),
      });
    }

    // Picture Frame add-on.
    var frameToggle = form.querySelector('[data-commerce-v2-frame-toggle]');
    var frameOptions = form.querySelector('[data-commerce-v2-frame-options]');
    var framePriceEl = form.querySelector('[data-commerce-v2-frame-price]');
    var framePreviewImg = form.querySelector('[data-commerce-v2-frame-preview]');
    if (frameToggle && frameOptions) {
      frameToggle.addEventListener('change', function () {
        frameOptions.hidden = !frameToggle.checked;
        updateTotal();
      });
    }

    // Phase 2C.2A — for the PARTNER memorial offer only (1 Vault/Capsule
    // PLUS 1 Picture Frame, included, non-removable — an authoritative
    // business decision, never a frontend-only default), force the
    // add-on ON and make the checkbox non-interactive. Frame COLOR
    // remains fully selectable — only the include/exclude toggle is
    // locked. Applied via refreshFrameLock() (defined above), driven by
    // root._partnerActive — the SAME root-level flag every panel and
    // every relevant state change (variant, submit, memorial-type
    // switch) consults, rather than each panel independently awaiting
    // its own resolveContext().then() the moment initPanel() happens to
    // run. A direct, non-partner BPF customer never has
    // root._partnerActive become true, so this never runs for them —
    // the existing optional-frame behavior is completely unchanged.
    //
    // Frontend enforcement alone is NOT the security boundary — see
    // partner-draft.ts's own server-side requirement check, which
    // rejects a browser-tampered "no frame" partner draft regardless
    // of what this UI shows.

    // Phase 8C, Part C — the checkbox's own small preview image always
    // reflects whichever frame color is currently selected, whether or
    // not the add-on is checked (so unchecking and re-checking never
    // shows a stale image from a different color).
    function updateFramePreview() {
      var checkedColor = form.querySelector('[data-frame-variant-id]:checked');
      if (!checkedColor || !framePreviewImg) return;
      var imageUrl = checkedColor.getAttribute('data-frame-image');
      var label = checkedColor.getAttribute('data-frame-label') || '';
      if (imageUrl) {
        framePreviewImg.src = imageUrl;
        framePreviewImg.alt = 'Memorial Picture Frame — ' + label;
      }
    }

    if (framePriceEl) {
      var updateFramePrice = function () {
        var checkedColor = form.querySelector('[data-frame-variant-id]:checked');
        var cents = checkedColor ? parseInt(checkedColor.getAttribute('data-frame-price'), 10) : NaN;
        if (!isNaN(cents)) framePriceEl.textContent = '+' + formatMoney(cents);
      };
      form.querySelectorAll('[data-frame-variant-id]').forEach(function (input) {
        input.addEventListener('change', function () {
          updateFramePrice();
          updateFramePreview();
          updateTotal();
        });
      });
      updateFramePrice();
      updateFramePreview();
    }

    // Phase 8C, Part D — "View frame details" opens the shared,
    // non-Builder <modal-dialog> component, syncing its image to
    // whichever frame color is CURRENTLY selected every time it opens
    // (never a separate, possibly-stale modal state).
    var frameDetailsTrigger = form.querySelector('[data-commerce-v2-frame-details-trigger]');
    if (frameDetailsTrigger) {
      frameDetailsTrigger.addEventListener('click', function () {
        var modalId = frameDetailsTrigger.getAttribute('data-frame-modal-target');
        var modal = modalId ? document.getElementById(modalId) : null;
        if (!modal) return;
        var modalImg = modal.querySelector('[data-commerce-v2-frame-modal-image]');
        var checkedColor = form.querySelector('[data-frame-variant-id]:checked');
        if (modalImg && checkedColor) {
          var imageUrl = checkedColor.getAttribute('data-frame-image');
          var label = checkedColor.getAttribute('data-frame-label') || '';
          if (imageUrl) {
            modalImg.src = imageUrl;
            modalImg.alt = 'Memorial Picture Frame — ' + label;
          }
        }
        if (typeof modal.show === 'function') modal.show(frameDetailsTrigger);
      });
    }

    function getFrameItem() {
      if (!frameToggle || !frameToggle.checked) return null;
      var checkedColor = form.querySelector('[data-frame-variant-id]:checked');
      if (!checkedColor) return null;
      var frameVariantId = checkedColor.getAttribute('data-frame-variant-id');
      if (!frameVariantId) return null;
      return { id: parseInt(frameVariantId, 10), quantity: 1 };
    }

    // Partner Preview Phase 1, root-cause fix — the confirmed gap the
    // pre-live audit found: Picture Frame is a SEPARATE cart line item
    // (built by getFrameItem() above), never a properties[...] field on
    // the main item, so it was silently absent from the partner draft
    // payload entirely. This reads the EXACT SAME already-resolved DOM
    // state getFrameItem() itself reads (frameToggle.checked, the
    // checked [data-frame-variant-id] color radio) — never a second/
    // independent frame-variant resolver — and additionally surfaces
    // the frame's own product id (data-commerce-v2-frame-product-id,
    // read-only, added alongside this fix) and the selected color's
    // human-readable label (data-frame-label, already existing) so a
    // future restore flow has everything needed without re-deriving
    // anything. When no frame is selected, returns a complete,
    // unambiguous "not selected" shape — never simply omitted, so a
    // saved draft can always distinguish "no frame chosen" from
    // "frame data missing/not yet captured."
    function getFramePayload() {
      var selected = Boolean(frameToggle && frameToggle.checked);
      if (!selected) {
        return { selected: false, productId: null, variantId: null, properties: {} };
      }
      var frameWrap = form.querySelector('[data-commerce-v2-frame]');
      var checkedColor = form.querySelector('[data-frame-variant-id]:checked');
      return {
        selected: true,
        productId: frameWrap ? frameWrap.getAttribute('data-commerce-v2-frame-product-id') : null,
        variantId: checkedColor ? checkedColor.getAttribute('data-frame-variant-id') : null,
        properties: checkedColor && checkedColor.getAttribute('data-frame-label')
          ? { Color: checkedColor.getAttribute('data-frame-label') }
          : {},
      };
    }

    function collectProperties() {
      var properties = {};
      // Phase 8J, Part U/V — the Engraving Color property may ONLY ever
      // be submitted while the Engraved Pet Name is non-empty once
      // trimmed. This is the authoritative gate: a previously-checked
      // color radio is deliberately left checked (never uncheck it) so
      // the customer's choice survives if they re-type a name in the
      // same session, but it must still never reach the cart on its
      // own if the name is currently blank.
      var trimmedPetName = petNameInput ? petNameInput.value.trim() : '';
      form.querySelectorAll('[name^="properties["]').forEach(function (field) {
        if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) return;
        var match = /^properties\[(.+)\]$/.exec(field.name);
        if (!match) return;
        if (match[1] === 'Engraving Color' && trimmedPetName === '') return;
        var value = field.value;
        if (value === '' || value === null || value === undefined) return;
        properties[match[1]] = value;
      });
      return properties;
    }

    function setLoading(isLoading) {
      if (!submitButton) return;
      isSubmitting = isLoading;
      submitButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
      submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      refreshSubmitLabel();
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
    }

    function showError(message) {
      if (!errorEl) return;
      if (!message) {
        errorEl.hidden = true;
        errorEl.textContent = '';
        return;
      }
      errorEl.hidden = false;
      errorEl.textContent = message;
    }

    // Phase 8C, Part B — Memorial Statue required confirmation.
    // Independently reimplemented (never calling Builder JS): the
    // exact same real business condition (properties[Capsule Type] ==
    // "Memorial Statue") pauses submission until the customer confirms
    // in the SAME shared, non-Builder <modal-dialog> component the
    // Builder's own equivalent popup already proves is safe to reuse.
    // Memorial Urn (and every Vault panel, which has no Capsule Type
    // field at all) never triggers it.
    var isCapsulePanel = panelEl.getAttribute('data-memorial-type') === 'capsule';
    var statueBypass = false;
    var statueModal = isCapsulePanel ? document.querySelector('[data-commerce-v2-statue-modal]') : null;

    function requiresStatueConfirmation() {
      if (!isCapsulePanel) return false;
      var capsuleType = form.querySelector('[name="properties[Capsule Type]"]:checked');
      return Boolean(capsuleType && capsuleType.value === 'Memorial Statue');
    }

    if (statueModal) {
      var statueConfirmBtn = statueModal.querySelector('[data-commerce-v2-statue-confirm]');
      var statueCancelBtns = statueModal.querySelectorAll('[data-commerce-v2-statue-cancel]');
      if (statueConfirmBtn) {
        statueConfirmBtn.addEventListener('click', function () {
          if (typeof statueModal.hide === 'function') statueModal.hide();
          // One-shot resume flag, consumed at the very top of the next
          // submit — resuming via a REAL synthetic submit on the same
          // form/button (never a second, parallel submission path) so
          // every other guard (double-submit lock, validation) still
          // applies exactly once.
          statueBypass = true;
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitButton);
          } else if (submitButton) {
            submitButton.click();
          }
        });
      }
      statueCancelBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          // Part: cancel produces zero cart request and leaves every
          // selection exactly as the customer left it.
          if (typeof statueModal.hide === 'function') statueModal.hide();
        });
      });
    }

    // Phase 8G, Part root cause — the Phase 8C/8E two-stage approach
    // (a plain `{items:[...]}` JSON POST, then a SEPARATE Section
    // Rendering GET) was built to avoid a DIFFERENT, real anti-pattern:
    // combining a multi-item JSON body with `sections`/`sections_url`
    // in ONE JSON POST (Phase 8B's own documented blank-drawer cause).
    // But Phase 8E's own URL-normalization fix for that two-stage GET
    // (using window.location.pathname) did NOT fix Memorial Product
    // V2's blank drawer — proving the two-stage GET itself, not just
    // its URL, was the fragile surface for a product-template route.
    //
    // Read-only audit of this THEME'S OWN generic, already-proven
    // product-page Add-to-Cart flow (assets/product-form.js) found it
    // uses neither of the above: it builds a single FormData POST
    // (bracket-array syntax for the variant id/quantity/properties —
    // Shopify's own documented Ajax API format, NOT a JSON body) with
    // `sections` and `sections_url` appended to that SAME FormData,
    // and calls `cart.renderContents(response)` directly on the single
    // response — no second request at all. Critically, it also
    // explicitly deletes the default `Content-Type: application/json`
    // header before sending, because a FormData body needs the
    // browser's own multipart boundary header, not a JSON one — a
    // mismatched Content-Type on a FormData body is a well-documented
    // way for a server to silently fail to parse the request correctly.
    //
    // This is the theme's OWN native, single-request contract — used
    // for exactly this class of request (product-page add-to-cart-then-
    // populate-drawer) on every ordinary product page already. Adopting
    // it here (rather than a second, custom two-stage V2 implementation)
    // removes the entire fragile "separate GET against some URL" surface
    // that Phase 8E's fix narrowed but did not eliminate, and gives
    // Collection V2 and Product V2 the SAME one reliable primitive
    // (Part N) instead of two nearly-identical implementations.
    //
    // On top of that proven request contract, this file keeps its own
    // STRICTER safety layer the generic code does not have: every
    // returned section string is parsed and checked for the exact
    // element `cart.renderContents()`'s own getSectionInnerHTML() will
    // later look for (Part E — a non-empty string is not proof the
    // right markup is inside it) BEFORE renderContents() is ever
    // called, so a malformed/incomplete response can never reach the
    // generic renderer, throw there, or open a visibly blank drawer.
    function buildCartAddFormData(items) {
      var formData = new FormData();
      items.forEach(function (item, index) {
        formData.append('items[' + index + '][id]', item.id);
        formData.append('items[' + index + '][quantity]', item.quantity);
        if (item.properties) {
          Object.keys(item.properties).forEach(function (key) {
            formData.append('items[' + index + '][properties][' + key + ']', item.properties[key]);
          });
        }
      });
      return formData;
    }

    function sectionHtmlHasSelector(html, selector) {
      if (typeof html !== 'string' || html.length === 0) return false;
      try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return Boolean(doc.querySelector(selector || '.shopify-section'));
      } catch (e) {
        return false;
      }
    }

    // Phase 8H, Part G/M — a section string can pass the Phase 8G
    // selector check (the expected element genuinely exists) while
    // still being the WRONG content: real merchant QA proved the
    // Phase 8G single-request fix did not resolve Memorial Product
    // V2's blank drawer even though the underlying cart mutation is
    // proven correct (the item is later visible from a fresh homepage
    // load). The one concrete, testable difference between "mutation
    // correct" and "drawer shows nothing useful" is that the SAME
    // response's `sections` can reflect the cart-drawer's own EMPTY-
    // CART markup (snippets/cart-drawer.liquid renders `is-empty` on
    // both <cart-drawer> and <cart-drawer-items> whenever `cart ==
    // empty`) if Shopify renders those sections from a cart snapshot
    // taken before this exact request's own mutation is fully visible
    // to it — a response can be non-empty and contain the right
    // element while still describing a cart that doesn't yet include
    // what was just added. This is only ever meaningful to check RIGHT
    // after a successful add (never on general principle), since an
    // `is-empty` cart-drawer section is completely correct in every
    // other context.
    function sectionHtmlLooksLikeEmptyCart(html) {
      if (typeof html !== 'string' || html.length === 0) return false;
      try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return Boolean(doc.querySelector('cart-drawer.is-empty, cart-drawer-items.is-empty'));
      } catch (e) {
        return false;
      }
    }

    // Phase 8I — root cause of both the reported blank-drawer-body
    // symptom AND the console TypeError. Read-only audit of assets/
    // component-cart-drawer.css found `cart-drawer.is-empty
    // .drawer__footer { display: none; }` — the entire populated cart
    // area (items, subtotal, checkout) stays CSS-hidden regardless of
    // what HTML is actually inside it. Read-only audit of assets/cart-
    // drawer.js's own open() found it ALSO branches on that exact class
    // to choose its focus-trap target: `this.classList.contains
    // ('is-empty') ? this.querySelector('.drawer__inner-empty') :
    // document.getElementById('CartDrawer')`. renderContents() only
    // ever replaces #CartDrawer's own innerHTML — it never touches the
    // PERSISTENT outer <cart-drawer> custom element's own `is-empty`
    // class, which Shopify only ever sets server-side once, at initial
    // page load (snippets/cart-drawer.liquid: `class="cart-drawer-
    // container{% if cart == empty %} is-empty{% endif %}"`). On a page
    // that started with a genuinely empty cart, that class survives
    // untouched through an AJAX-populated render: the CSS keeps the
    // populated footer hidden, AND once the fresh #CartDrawer HTML no
    // longer contains .drawer__inner-empty at all (the cart isn't empty
    // any more), open()'s ternary resolves to null — trapFocus(null,
    // ...) -> getFocusableElements(null) -> null.querySelectorAll
    // throws exactly the reported TypeError. Both symptoms share this
    // one root cause.
    //
    // Fixed by synchronizing the SAME `is-empty` class (the theme's own
    // existing convention — never an invented marker) on both the outer
    // <cart-drawer> and its nested <cart-drawer-items> (which the same
    // Liquid `cart == empty` condition also marks) to match the
    // ALREADY-VALIDATED section response's real content — the response
    // is the authority, never an assumption that "an add succeeded so
    // the cart must be non-empty" — reusing sectionHtmlLooksLikeEmptyCart
    // (Phase 8H) as the single source of truth rather than a second,
    // divergent empty/populated check. Called immediately before
    // cart.renderContents() (never on a delay — its own open() call
    // reads this class synchronously as part of the very same call).
    function syncDrawerEmptyState(cart, cartDrawerSectionHtml) {
      var populated = !sectionHtmlLooksLikeEmptyCart(cartDrawerSectionHtml);
      cartDebug('syncDrawerEmptyState() before', {
        drawerClass: cart.className,
        wasEmpty: cart.classList.contains('is-empty'),
        hasEmptyDrawerContainer: Boolean(cart.querySelector('.drawer__inner-empty')),
        hasCartDrawerTarget: Boolean(document.getElementById('CartDrawer')),
        responseIndicatesPopulated: populated,
      });
      cart.classList.toggle('is-empty', !populated);
      var itemsEl = cart.querySelector('cart-drawer-items');
      if (itemsEl) itemsEl.classList.toggle('is-empty', !populated);
      cartDebug('syncDrawerEmptyState() after (before renderContents)', {
        drawerClass: cart.className,
        isEmpty: cart.classList.contains('is-empty'),
      });
    }

    function hasRequiredCartSections(sections, sectionsToRender) {
      if (!sections) return false;
      return sectionsToRender.every(function (section) {
        return sectionHtmlHasSelector(sections[section.id], section.selector);
      });
    }

    // Phase 8H, Part M — restores a SEPARATE, read-only Section
    // Rendering GET as an explicit FALLBACK ONLY (never the primary
    // path), used exactly once, only when the primary single-request
    // response's sections fail validation or look like a stale/empty
    // cart snapshot immediately after a proven-successful add. A GET
    // issued after the POST has already resolved is guaranteed to see
    // the fully-committed cart state, eliminating the specific race
    // sectionHtmlLooksLikeEmptyCart() above is written to catch.
    function fetchFreshCartSections(sectionsToRender) {
      var url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', sectionsToRender.map(function (section) { return section.id; }).join(','));
      cartDebug('fallback: fetching fresh cart sections', { url: url.toString() });
      return fetch(url.toString(), { method: 'GET' }).then(function (response) {
        return response.json();
      });
    }

    var submitInFlight = false;
    var submitEntryCount = 0;
    var renderContentsCallCount = 0;

    function renderCartDrawer(cart, sections) {
      renderContentsCallCount += 1;
      var cartDrawerTarget = document.getElementById('CartDrawer');
      cartDebug('renderCartDrawer() call #' + renderContentsCallCount, {
        cartDrawerInstanceCount: document.querySelectorAll('cart-drawer').length,
        targetExistsBeforeRender: Boolean(cartDrawerTarget),
        targetInnerHTMLLengthBeforeRender: cartDrawerTarget ? cartDrawerTarget.innerHTML.length : null,
      });
      // Must run BEFORE renderContents(), never on a delay — open()
      // (called synchronously from inside renderContents()) reads the
      // outer <cart-drawer>'s own is-empty class immediately to decide
      // its focus-trap target.
      syncDrawerEmptyState(cart, sections['cart-drawer']);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      try {
        cart.renderContents({ sections: sections });
      } catch (e) {
        cartDebug('renderContents() threw', { message: e && e.message });
        throw e;
      }
      cartDebug('renderCartDrawer() call #' + renderContentsCallCount + ' — after render', {
        drawerClass: cart.className,
        isEmpty: cart.classList.contains('is-empty'),
        hasEmptyDrawerContainer: Boolean(cart.querySelector('.drawer__inner-empty')),
        hasCartDrawerTarget: Boolean(document.getElementById('CartDrawer')),
      });
      if (cartDrawerTarget) {
        scheduleDrawerLengthChecks(cartDrawerTarget, 'renderCartDrawer() call #' + renderContentsCallCount);
        observeDrawerForClearing(cartDrawerTarget, 500);
      }
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      submitEntryCount += 1;
      cartDebug('submit handler entered', { entryCount: submitEntryCount, memorialType: panelEl.getAttribute('data-memorial-type') });
      // Double-submit protection — a second Enter/click while a
      // request is already in flight (or while aria-disabled from a
      // prior sold-out/loading state) is a silent no-op, never a
      // second request.
      if (submitInFlight) return;
      if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;

      var bypassStatue = statueBypass;
      statueBypass = false;

      if (!bypassStatue && requiresStatueConfirmation()) {
        if (statueModal && typeof statueModal.show === 'function') {
          statueModal.show(submitButton);
        }
        return;
      }

      showError(null);

      var mainVariantId = variantIdInput ? parseInt(variantIdInput.value, 10) : null;
      if (!mainVariantId) {
        showError('Please choose an option before adding to cart.');
        return;
      }

      // Phase 8J, Part T — no established canonical default Engraving
      // Color exists to fall back to (read-only audited: the Memorial
      // Builder's own default is a per-breed/style visual-mapping value
      // V2 has no equivalent of), so a non-empty name REQUIRES an
      // explicit color choice before submitting — inline V2 validation,
      // never a browser alert().
      if (engravingColorWrap && petNameInput && petNameInput.value.trim() !== '') {
        var checkedEngravingColor = engravingColorWrap.querySelector('[data-commerce-v2-engraving-color-input]:checked');
        if (!checkedEngravingColor) {
          showError('Please choose an Engraving Color.');
          var firstEngravingColorInput = engravingColorInputs[0];
          if (firstEngravingColorInput) firstEngravingColorInput.focus();
          return;
        }
      }

      // Partner Preview Phase 1 — every validation above (variant chosen,
      // Memorial Statue confirmation, Engraving Color requirement) has
      // already run and passed by this point, completely unmodified. In
      // partner mode the ONLY thing that changes is what happens next:
      // instead of the real /cart/add.js fetch below, hand the exact
      // same validated variant id + collectProperties() result (the
      // SAME function a real submission uses) to the shared partner-mode
      // module, which owns the save/modal/redirect flow. isActive() is
      // false (a no-op fall-through to the real cart-add below) for
      // every normal BPF customer and for any page that never carried a
      // partner context at all.
      //
      // addons.pictureFrame (root-cause fix, pre-live audit): reuses
      // getFramePayload() above — the exact same DOM state getFrameItem()
      // itself reads for the real, un-diverted /cart/add.js multi-item
      // request — so the saved draft can always faithfully recreate
      // whichever Picture Frame line (or explicit absence of one) the
      // real Add to Cart would otherwise have submitted.
      if (window.BPFPartnerMode && window.BPFPartnerMode.isActive()) {
        // Defensive re-assertion, right at the moment of save: isActive()
        // being true here already guarantees resolveContext() settled
        // valid, so root._partnerActive is already true and every
        // panel's frame was already locked once by initPartnerSync()/
        // setActiveType()'s own refresh calls — this call is a
        // deliberately redundant, zero-cost guarantee (refreshFrameLock()
        // is idempotent) that the exact panel about to be saved reports
        // selected=true, rather than trusting that an earlier async
        // refresh has already visually landed on THIS panel by now.
        refreshFrameLock();
        // Root-cause fix (real order #1137 gap): variantId is sent as
        // variantIdInput.value (a STRING, same as productId's own
        // getAttribute() above) rather than mainVariantId (a JS number
        // from parseInt(), used only for the real-cart-add validation
        // above). InsightOS's own request parsing (asOptionalString())
        // only ever accepts a string — a number silently became null
        // there, saving a draft with a real productId but no variantId
        // at all, even though the customer's selection was already
        // fully and correctly resolved right here. Never re-resolve the
        // variant a second way — variantIdInput.value IS that same
        // already-validated value (see the mainVariantId check above),
        // just sent in the type the save endpoint actually accepts.
        //
        // properties also merges in collectSelectedVariantOptions() —
        // each REAL Shopify option's own current value (e.g. Vault's
        // "Color"/"Vault Color") — so both variant dimensions survive
        // the save as structured data, never only as the free-text
        // "Engraving Color" property. Purely additive: collectProperties()
        // itself, and the real /cart/add.js path below, are unchanged.
        window.BPFPartnerMode.handleReturnToPartner(
          {
            animal: panelEl.getAttribute('data-animal-type') || null,
            breed: panelEl.getAttribute('data-breed') || null,
            memorialType: panelEl.getAttribute('data-memorial-type') || null,
            productId: panelEl.getAttribute('data-product-id') || null,
            variantId: variantIdInput ? variantIdInput.value : null,
            properties: Object.assign({}, collectProperties(), collectSelectedVariantOptions()),
            addons: { pictureFrame: getFramePayload() },
          },
          submitButton
        );
        return;
      }

      var frameVariantForDebug = form.querySelector('[data-frame-variant-id]:checked');
      cartDebug('resolved selection', {
        href: window.location.href,
        pathname: window.location.pathname,
        mode: root.getAttribute('data-mode'),
        mainVariantId: mainVariantId,
        frameVariantId: frameToggle && frameToggle.checked && frameVariantForDebug ? frameVariantForDebug.getAttribute('data-frame-variant-id') : null,
      });

      var items = [
        {
          id: mainVariantId,
          quantity: 1,
          properties: collectProperties(),
        },
      ];
      var frameItem = getFrameItem();
      if (frameItem) items.push(frameItem);

      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var sectionsToRender = cart && typeof cart.getSectionsToRender === 'function' ? cart.getSectionsToRender() : null;

      if (isCartDebugEnabled()) {
        var allDrawers = document.querySelectorAll('cart-drawer');
        cartDebug('cart-drawer instance audit', {
          instanceCount: allDrawers.length,
          instances: Array.prototype.map.call(allDrawers, function (el, idx) {
            return { index: idx, id: el.id || null, className: el.className, isQueriedInstance: el === cart };
          }),
          sectionsToRender: sectionsToRender,
        });
      }

      var formData = buildCartAddFormData(items);
      if (sectionsToRender) {
        formData.append('sections', sectionsToRender.map(function (section) { return section.id; }).join(','));
        formData.append('sections_url', window.location.pathname);
      }

      submitInFlight = true;
      setLoading(true);

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (response) {
          if (response && response.status) {
            // Genuine Shopify-reported cart error (e.g. sold out) —
            // Part: never open a blank/empty drawer; restore the
            // button, show an inline error, keep every selection.
            // Nothing was added, so this is the ONE branch that still
            // shows the generic "something went wrong" family of error.
            cartDebug('cart/add.js reported an error', { status: response.status, description: response.description, message: response.message });
            showError(response.description || response.message || 'This item could not be added to your cart.');
            return;
          }

          if (isCartDebugEnabled()) {
            var sectionKeys = response.sections ? Object.keys(response.sections) : [];
            cartDebug('cart/add.js response received', {
              topLevelKeys: Object.keys(response || {}),
              hasSections: Boolean(response.sections),
              sectionKeys: sectionKeys,
              sectionHtmlLengths: sectionKeys.reduce(function (acc, key) {
                acc[key] = (response.sections[key] || '').length;
                return acc;
              }, {}),
              sectionSelectorValidation: (sectionsToRender || []).reduce(function (acc, section) {
                acc[section.id] = {
                  selector: section.selector || '.shopify-section',
                  found: sectionHtmlHasSelector(response.sections && response.sections[section.id], section.selector),
                };
                return acc;
              }, {}),
            });
          }

          // Stage 1 (the actual cart mutation) is now CONFIRMED
          // successful — the item(s) ARE in the real cart. Everything
          // from here on is only a DISPLAY problem; this nested chain
          // gets its OWN .catch() below that ALWAYS falls back to the
          // real cart page rather than the generic inline "something
          // went wrong" error, since telling the customer the add
          // itself failed would be false — it already succeeded.
          if (!cart || !sectionsToRender || typeof cart.renderContents !== 'function') {
            // No drawer/notification component present on this page at
            // all — send the customer to the real cart page rather
            // than silently doing nothing.
            cartDebug('no drawer/notification component on this page — redirecting to /cart');
            window.location = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }

          var primarySections = response.sections;
          var primaryValid = hasRequiredCartSections(primarySections, sectionsToRender);
          var primaryLooksEmpty = primaryValid && (primarySections['cart-drawer'] ? sectionHtmlLooksLikeEmptyCart(primarySections['cart-drawer']) : false);

          if (primaryValid && !primaryLooksEmpty) {
            cartDebug('primary single-request sections valid and non-stale — rendering immediately');
            renderCartDrawer(cart, primarySections);
            return;
          }

          cartDebug('primary sections unusable — falling back to a fresh Section Rendering GET', { primaryValid: primaryValid, primaryLooksEmpty: primaryLooksEmpty });

          return fetchFreshCartSections(sectionsToRender)
            .then(function (freshSections) {
              var freshValid = hasRequiredCartSections(freshSections, sectionsToRender);
              cartDebug('fallback fresh sections received', { freshValid: freshValid });
              if (!freshValid) {
                // The item is already safely in the cart — never show
                // a blank/broken drawer; the real cart page always
                // reflects the true cart state.
                window.location = (window.routes && window.routes.cart_url) || '/cart';
                return;
              }
              renderCartDrawer(cart, freshSections);
            })
            .catch(function (e) {
              // The fallback GET itself failed (network error) — the
              // item is still already safely in the cart.
              cartDebug('fallback fetch failed', { message: e && e.message });
              window.location = (window.routes && window.routes.cart_url) || '/cart';
            });
        })
        .catch(function (e) {
          // Stage 1 itself failed (network error, bad response, etc.)
          // — nothing was added. Restore the button, show an inline
          // error, keep every selection intact for retry.
          cartDebug('Stage 1 (cart mutation) request failed', { message: e && e.message });
          showError('Something went wrong adding this to your cart. Please try again.');
        })
        .finally(function () {
          submitInFlight = false;
          setLoading(false);
        });
    });

    updateFromVariant();
    updateTotal();

    // Returned so initRoot() can collect one refresher per panel and
    // re-apply partner state (frame lock + CTA label) from a single
    // root-level call — once resolveContext() settles, and again after
    // every memorial-type switch (setActiveType()) — instead of each
    // panel racing its own independent promise callback.
    return function refreshPartnerState() {
      refreshFrameLock();
      refreshSubmitLabel();
    };
  }

  // ----- Memorial Type switch (Breed Collection V2 only, both eligible) -----
  function initTypeSwitch(root) {
    var switchEl = root.querySelector('[data-commerce-v2-type-switch]');
    if (!switchEl) return;

    var buttons = Array.prototype.slice.call(switchEl.querySelectorAll('[data-memorial-type-option]'));

    function setActiveType(type) {
      buttons.forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-memorial-type-option') === type ? 'true' : 'false');
      });
      root.querySelectorAll('[data-commerce-v2-media]').forEach(function (el) {
        var isTarget = el.getAttribute('data-memorial-type') === type;
        el.hidden = !isTarget;
        // Phase 8C, Part G — every switch resets the NEWLY-shown
        // gallery to its own server-resolved default hero, so a
        // manual thumbnail pick from an earlier visit to this same
        // product never lingers as a surprise on return.
        if (isTarget && el._resetToInitialHero) el._resetToInitialHero();
      });
      var activePanel = null;
      root.querySelectorAll('[data-commerce-v2-panel]').forEach(function (el) {
        var isTarget = el.getAttribute('data-memorial-type') === type;
        el.hidden = !isTarget;
        if (isTarget) activePanel = el;
      });
      root.setAttribute('data-active-type', type);

      // The `hidden` toggles just above move the newly-active panel's
      // sticky-CTA sentinel straight from a zero-size, non-
      // participating box to its real on-screen position without any
      // scrolling — initStickyCta()'s IntersectionObserver isn't
      // reliably fired by that alone (confirmed: this was the exact
      // cause of the sticky Add to Cart only reappearing after the
      // shopper scrolled). One requestAnimationFrame after the hidden/
      // visible swap above (so the browser has actually laid out the
      // now-visible panel before it's measured) re-evaluates every
      // dock via the SAME observer/entries this file already
      // maintains — no second observer, no second CTA, and a no-op
      // wherever this property was never set (mode without any CTA
      // dock at all). The same rAF also re-asserts root-level partner
      // state (frame lock + CTA label) on both panels — deterministic,
      // no arbitrary timers — so a switch can never leave the
      // now-visible panel showing a stale label or an unlocked frame.
      if (typeof root._reevaluateStickyCta === 'function' || typeof root._refreshPartnerState === 'function') {
        window.requestAnimationFrame(function () {
          if (typeof root._reevaluateStickyCta === 'function') root._reevaluateStickyCta();
          if (typeof root._refreshPartnerState === 'function') root._refreshPartnerState();
        });
      }

      // Partner Preview Phase 1 — track which memorial type (Vault vs
      // Capsule) a partner-context customer ends up viewing, whenever
      // they explicitly use the switcher (the two-eligible-styles
      // case). The corresponding initial/auto-selected-style fire lives
      // in sections/bpf-memorial-commerce-v2.liquid's own partner-mode
      // init block, since that already runs once per page load
      // regardless of whether a switcher is even present. Fires only in
      // genuinely confirmed partner mode — a complete no-op, zero
      // network activity, for every normal BPF customer.
      if (window.BPFPartnerMode && window.BPFPartnerMode.isActive() && activePanel) {
        window.BPFPartnerMode.dispatchPartnerEvent('partner_memorial_selected', {
          partner_id: window.BPFPartnerMode.getPartnerId(),
          animal: activePanel.getAttribute('data-animal-type'),
          breed: activePanel.getAttribute('data-breed'),
          memorial_type: activePanel.getAttribute('data-memorial-type'),
        });
      }
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setActiveType(btn.getAttribute('data-memorial-type-option'));
      });
    });
  }

  // Partner mode, root-level: a single flag on the root element that
  // every panel's refreshPartnerState() (CTA label + frame lock)
  // consults, resolved exactly once (resolveContext() is itself
  // memoized) and then re-applied to EVERY panel — never re-resolved,
  // never re-decided, per panel. Zero network activity and root.
  // _partnerActive permanently false/absent for any page without
  // window.BPFPartnerMode or without a partner param at all, so normal
  // BPF traffic is completely unaffected.
  function initPartnerSync(root, panelRefreshers) {
    if (!window.BPFPartnerMode || !window.BPFPartnerMode.hasPartnerParam) return;
    window.BPFPartnerMode.resolveContext().then(function (ctx) {
      root._partnerActive = Boolean(ctx && ctx.valid);
      panelRefreshers.forEach(function (refresh) {
        refresh();
      });
    });
  }

  function initRoot(root) {
    root._partnerActive = false;
    applySwatchColors(root);
    measureStickyOffset(root);
    initMobileDock(root);
    initStickyCta(root);

    root.querySelectorAll('[data-commerce-v2-media]').forEach(function (mediaEl) {
      initMedia(mediaEl);
    });
    var panelRefreshers = [];
    root.querySelectorAll('[data-commerce-v2-panel]').forEach(function (panelEl) {
      var refresh = initPanel(panelEl, root);
      if (typeof refresh === 'function') panelRefreshers.push(refresh);
    });
    initTypeSwitch(root);
    root._refreshPartnerState = function () {
      panelRefreshers.forEach(function (refresh) {
        refresh();
      });
    };
    initPartnerSync(root, panelRefreshers);

    var scheduleRemeasure = debounce(function () {
      measureStickyOffset(root);
      // See initStickyCta()'s own comment: a viewport-height change
      // alone can require a re-dock/undock decision without the
      // sentinel ever re-crossing 0% visibility on its own.
      if (typeof root._reevaluateStickyCta === 'function') root._reevaluateStickyCta();
    }, 150);
    window.addEventListener('resize', scheduleRemeasure);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleRemeasure);
    }
  }

  function init() {
    var roots = document.querySelectorAll('[data-commerce-v2]');
    if (!roots.length) return; // Never initializes on a page without a V2 section.
    roots.forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
